import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useToastStore } from '@/platform/updates/common/toastStore'
import {
  type LoginRequest,
  type RegisterRequest,
  type User,
  useFastApiAuthService
} from '@/services/fastapiAuthService'

// Global flag to ensure initialize is only called once
let globalAuthInitialized = false
let globalAuthInitializing = false

export const useAuthStore = defineStore('auth', () => {
  const authService = useFastApiAuthService()
  const toastStore = useToastStore()

  // State
  const currentUser = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(
    () => !!currentUser.value && !!accessToken.value
  )
  const userDisplayName = computed(() => currentUser.value?.username || '')
  const userEmail = computed(() => currentUser.value?.email || '')
  const isAdmin = computed(() => currentUser.value?.is_admin || false)

  // Initialize auth state from localStorage
  const initialize = async (_skipLoginDialog = false) => {
    // Return early if already initialized
    if (globalAuthInitialized) {
      isInitialized.value = true
      return
    }

    // Return early if already initializing
    if (globalAuthInitializing) {
      // Wait for initialization to complete
      while (globalAuthInitializing) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      isInitialized.value = true
      return
    }

    globalAuthInitializing = true

    try {
      console.log('AuthStore: Starting initialization...')

      // 重置加载状态
      authService.resetLoadingState()

      // Load tokens from localStorage
      const storedAccessToken = localStorage.getItem('fastapi_access_token')
      const storedRefreshToken = localStorage.getItem('fastapi_refresh_token')

      console.log('AuthStore: Tokens found in localStorage:', {
        hasAccessToken: !!storedAccessToken,
        hasRefreshToken: !!storedRefreshToken
      })

      if (storedAccessToken) {
        accessToken.value = storedAccessToken
        if (storedRefreshToken) {
          refreshToken.value = storedRefreshToken
        } else {
          console.log(
            'AuthStore: No refresh token found, but will try with access token only'
          )
        }

        // Verify token and get user info
        try {
          const user = await authService.getCurrentUser()
          if (user) {
            currentUser.value = user
            console.log(
              'AuthStore: Successfully initialized with user:',
              user.username
            )
          } else {
            // Token is invalid, clear it and show login dialog
            console.log(
              'AuthStore: Token verification failed, clearing auth data'
            )
            clearAuthData()
            // 不要在这里显示登录对话框，因为路由守卫会处理重定向
            // 在登录过期时直接让用户重定向到登录页，不显示弹窗
            console.log('登录已过期，将由路由守卫处理重定向')
          }
        } catch (error) {
          console.error('AuthStore: Failed to verify token:', error)
          clearAuthData()
          // Don't show login dialog during initialization - let router guard handle redirect
          console.log(
            'AuthStore: Token verification failed, will redirect to login page'
          )
        }
      } else {
        console.log(
          'AuthStore: No access token found, user is not authenticated'
        )
        // Don't show login dialog during initialization - let router guard handle redirect
        console.log('AuthStore: No token found, will redirect to login page')
      }
    } catch (error) {
      console.error('AuthStore: Failed to initialize auth:', error)
      clearAuthData()
    } finally {
      globalAuthInitialized = true
      globalAuthInitializing = false
      isInitialized.value = true
      console.log('AuthStore: Initialization completed')
    }
  }

  // Set authentication data
  const setAuthData = (
    user: User,
    accessTokenValue: string,
    refreshTokenValue: string
  ) => {
    currentUser.value = user
    accessToken.value = accessTokenValue
    refreshToken.value = refreshTokenValue

    // Store in localStorage
    localStorage.setItem('fastapi_access_token', accessTokenValue)
    localStorage.setItem('fastapi_refresh_token', refreshTokenValue)

    // 发出登录成功事件，通知其他组件
    window.dispatchEvent(new CustomEvent('auth-login-success'))
  }

  // Clear authentication data
  const clearAuthData = () => {
    currentUser.value = null
    accessToken.value = null
    refreshToken.value = null

    // Remove from localStorage
    localStorage.removeItem('fastapi_access_token')
    localStorage.removeItem('fastapi_refresh_token')

    // Reset global initialization flags
    globalAuthInitialized = false
    globalAuthInitializing = false
  }

  // Login
  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      const response = await authService.login(credentials)
      if (response) {
        // First, save the tokens to authStore
        accessToken.value = response.access_token
        localStorage.setItem('fastapi_access_token', response.access_token)
        // Note: Backend doesn't return refresh tokens, so we store a placeholder
        refreshToken.value = 'no-refresh-token' // Placeholder for backend compatibility
        localStorage.setItem('fastapi_refresh_token', 'no-refresh-token')

        // Then get user info with the saved token
        try {
          const user = await authService.getCurrentUser()
          if (user) {
            currentUser.value = user
            toastStore.add({
              severity: 'success',
              summary: '登录成功',
              detail: `欢迎, ${user.username}!`,
              life: 3000
            })
            // 发出登录成功事件，通知其他组件
            window.dispatchEvent(new CustomEvent('auth-login-success'))
            return true
          }
        } catch (userError) {
          console.error('Failed to get user info after login:', userError)
          // Even if user info fails, we still have valid tokens
          toastStore.add({
            severity: 'success',
            summary: '登录成功',
            detail: 'Token已保存，但获取用户信息失败',
            life: 3000
          })
          // 发出登录成功事件，通知其他组件
          window.dispatchEvent(new CustomEvent('auth-login-success'))
          return true
        }
      }
      // 登录失败：不重置加载状态以保留错误信息，且弹出错误提示
      toastStore.add({
        severity: 'error',
        summary: '登录失败',
        detail: authService.error.value || '用户名或密码错误',
        life: 5000
      })
      return false
    } catch (error: any) {
      // 登录失败，重置加载状态
      authService.resetLoadingState()
      toastStore.add({
        severity: 'error',
        summary: '登录失败',
        detail: error.message || '用户名或密码错误',
        life: 5000
      })
      return false
    }
  }

  // Register
  const register = async (userData: RegisterRequest): Promise<boolean> => {
    try {
      const response = await authService.register(userData)
      if (response) {
        toastStore.add({
          severity: 'success',
          summary: '注册成功',
          detail: '请使用您的凭据登录',
          life: 5000
        })
        return true
      }
      return false
    } catch (error: any) {
      toastStore.add({
        severity: 'error',
        summary: '注册失败',
        detail: error.message || '注册过程中发生错误',
        life: 5000
      })
      return false
    }
  }

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuthData()
      toastStore.add({
        severity: 'info',
        summary: '已登出',
        life: 3000
      })
    }
  }

  // Change password
  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> => {
    try {
      await authService.changePassword(oldPassword, newPassword)
      toastStore.add({
        severity: 'success',
        summary: '密码修改成功',
        life: 3000
      })
      return true
    } catch (error: any) {
      toastStore.add({
        severity: 'error',
        summary: '密码修改失败',
        detail: error.message || '未知错误',
        life: 5000
      })
      return false
    }
  }

  // Refresh user data
  const refreshUser = async (): Promise<void> => {
    if (isAuthenticated.value) {
      try {
        const user = await authService.getCurrentUser()
        if (user) {
          currentUser.value = user
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error)
      }
    }
  }

  // Refresh access token - currently not supported by backend
  const refreshAccessToken = async (): Promise<boolean> => {
    // Backend doesn't support refresh tokens, so this method always fails
    console.warn('Refresh token functionality not supported by backend')
    return false
  }

  // Get current access token
  const getAccessToken = (): string | null => {
    return accessToken.value
  }

  // Get current refresh token
  const getRefreshToken = (): string | null => {
    return refreshToken.value
  }

  // Debug method to check auth state
  const debugAuthState = () => {
    console.log('🔍 AuthStore Debug Info:', {
      isAuthenticated: isAuthenticated.value,
      currentUser: currentUser.value,
      accessToken: accessToken.value
        ? `${accessToken.value.substring(0, 20)}...`
        : null,
      refreshToken: refreshToken.value
        ? `${refreshToken.value.substring(0, 20)}...`
        : null,
      isInitialized: isInitialized.value,
      localStorage: {
        access: localStorage.getItem('fastapi_access_token')
          ? `${localStorage.getItem('fastapi_access_token')?.substring(0, 20)}...`
          : null,
        refresh: localStorage.getItem('fastapi_refresh_token')
          ? `${localStorage.getItem('fastapi_refresh_token')?.substring(0, 20)}...`
          : null
      }
    })
  }

  // Expose debug method globally for easy testing
  if (typeof window !== 'undefined') {
    // @ts-expect-error - window object is not typed in TypeScript environment
    window.debugAuth = debugAuthState
  }

  // 重置加载状态
  const resetLoadingState = () => {
    authService.resetLoadingState()
  }

  return {
    // State
    currentUser,
    accessToken,
    refreshToken,
    isInitialized,
    isLoading: authService.isLoading,
    error: authService.error,

    // Getters
    isAuthenticated,
    userDisplayName,
    userEmail,
    isAdmin,

    // Methods
    initialize,
    login,
    register,
    logout,
    changePassword,
    refreshUser,
    refreshAccessToken,
    getAccessToken,
    getRefreshToken,
    setAuthData,
    clearAuthData,
    resetLoadingState,
    debugAuthState
  }
})
